import { injectable } from 'inversify'
import Navigo from 'navigo'

/**
 * Dumb pipe around Navigo (invariant 12): no decisions, no URL building,
 * no URL parsing. Emits raw URLs in, pushes raw URLs out. The URL codec
 * lives in RouterRepository where the fast tests can see it.
 */
@injectable()
export class RouterGateway {
  private navigo: Navigo = null!

  // Captures the commit Promise `onUrlChange` returns for the match Navigo
  // just dispatched, so `navigate()` can await it — see there.
  private pendingCommit: Promise<boolean> | null = null

  listen = async (onUrlChange: (url: string) => Promise<boolean>) => {
    if (this.navigo) return new Promise((resolve) => setTimeout(resolve, 0))

    this.navigo = new Navigo('/', { hash: false, linksSelector: 'a', strategy: 'ONE' })

    this.navigo.on('*', (match) => {
      this.pendingCommit = onUrlChange(match!.url + (match!.queryString ? `?${match!.queryString}` : ''))
    })

    this.navigo.resolve()
    return new Promise((resolve) => setTimeout(resolve, 0))
  }

  /**
   * Resolves `true` only once the route transition this URL triggers has
   * actually committed — not merely once Navigo has dispatched the match.
   *
   * `Navigo.navigate()` invokes our `on('*')` handler synchronously, before
   * it touches history (the existing RESUME/PLAN comments in router.ts
   * already depend on that ordering), so `pendingCommit` is already set by
   * the time `navigo.navigate()` returns below; this only has to await it.
   * Before this existed, `navigate()` resolved as soon as Navigo *accepted*
   * the URL — while `updateCurrentRoute`'s own hooks were still pending —
   * which is why the real adapter and the test pivot used to mean different
   * things by "the navigation happened" (spec §15.1, §20 table).
   *
   * If a future Navigo version ever stops dispatching synchronously,
   * `pendingCommit` stays `null` here and this resolves `false` — the
   * real-adapter parity test in routing.infra.test.ts is the trip-wire for
   * that, not a silent behavior change.
   */
  navigate = async (url: string): Promise<boolean> => {
    this.pendingCommit = null
    this.navigo.navigate(url)
    return this.pendingCommit ?? false
  }

  /**
   * Rewrites the address bar and nothing else — no match dispatched, no
   * transition, no history entry.
   *
   * Deliberately *not* `navigate`: the caller is a list writing its own filters
   * into the URL, and running a real navigation for that would re-enter the
   * route on every keystroke (re-firing onEnter loads and the leave guard), and
   * would bury the user's actual history under one entry per character.
   */
  replaceUrl = (url: string): void => {
    this.navigo.navigate(url, {
      callHandler: false,
      updateBrowserURL: true,
      historyAPIMethod: 'replaceState',
    })
  }

  unload = () => {
    this.navigo?.destroy()
    this.navigo = null!
  }
}
